import { AlertTriangle, ClipboardList, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useQuestionnaire } from '../api/useQuestionnaire';
import {
  type QuestionnaireAnswers,
  type RenderableSection,
  toRenderableField,
  totalUploadBytes,
} from '../elements/answers';
import { QuestionnaireRenderer } from '../elements/QuestionnaireRenderer';

/**
 * A Questionnaire step's screen in the WORKFLOW builder's phone preview — the real form.
 *
 * THIS REPLACED A SUMMARY CARD that showed the questionnaire's name and a note saying answers are
 * recorded with the session. That was a deliberate trade at the time (avoid a fetch per step in an
 * editor that already loads the catalogue, customization and the workflow), and it was the wrong
 * call: the point of walking the phone preview is to see what the applicant sees, and a card with the
 * questionnaire's title told an admin nothing they could not read off the step list.
 *
 * The cost is one request per Questionnaire step, cached by TanStack Query and shared with the
 * questionnaire builder's own cache — so revisiting a step, or opening its builder, is free.
 *
 * NO LOGO, deliberately. The real run shows the brand mark only on the welcome screen; each step
 * after it carries its own title. Passing a logo here would preview a screen the applicant never
 * gets — see SessionMobilePanel.
 */
export function QuestionnaireStepPreview({
  questionnaireId,
  fallbackName,
}: {
  questionnaireId: string | null;
  /** The name cached on the step's settings — shown while loading, or if the fetch fails. */
  fallbackName: string | null;
}) {
  const { data, isLoading, error } = useQuestionnaire(questionnaireId ?? undefined);

  /**
   * Preview answers. Local, discarded, and interactive on purpose: the chips and cascades behave, so
   * an admin can tell the form works from here rather than only from the questionnaire builder.
   */
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});

  // The FIRST non-empty section. The workflow preview gives one screen per step, and paging within a
  // questionnaire belongs to the questionnaire's own builder — showing section 1 answers "what does
  // this step open with", which is what this preview is for.
  const section = useMemo<RenderableSection | null>(() => {
    const first = data?.sections.find((s) => s.fields.length > 0);
    if (!first) return null;
    return {
      id: first.id,
      title: first.title?.trim() || first.name,
      description: first.description ?? '',
      fields: first.fields.map(toRenderableField),
    };
  }, [data]);

  if (!questionnaireId) {
    return <Notice title="No questionnaire attached">Attach one in the step settings.</Notice>;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
        <p className="text-[12px] text-[#6B7280]">Loading {fallbackName ?? 'questionnaire'}…</p>
      </div>
    );
  }

  if (error) {
    return (
      <Notice title="Questionnaire unavailable" tone="warning">
        {fallbackName ? `"${fallbackName}" ` : 'It '}could not be loaded. It may have been deleted.
      </Notice>
    );
  }

  if (!section) {
    return (
      <Notice title={data?.name ?? fallbackName ?? 'Questionnaire'}>
        No questions yet — the applicant would see an empty form.
      </Notice>
    );
  }

  return (
    <div className="flex w-full flex-col items-center text-center">
      <QuestionnaireRenderer
        section={section}
        answers={answers[section.id] ?? {}}
        // Across every section, so the preview shows the same remaining upload budget an
        // applicant would see — the point of sharing one renderer.
        stepUploadBytes={totalUploadBytes(answers)}
        onChange={(fieldId, value) =>
          setAnswers((prev) => ({
            ...prev,
            [section.id]: { ...(prev[section.id] ?? {}), [fieldId]: value },
          }))
        }
      />
    </div>
  );
}

function Notice({
  title,
  children,
  tone = 'muted',
}: {
  title: string;
  children: React.ReactNode;
  tone?: 'muted' | 'warning';
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
        {tone === 'warning' ? (
          <AlertTriangle className="h-5 w-5 text-orangeDA" />
        ) : (
          <ClipboardList className="h-5 w-5 text-primary" />
        )}
      </span>
      <p className="text-[14px] font-medium text-[#111827]">{title}</p>
      <p className="max-w-[240px] text-[12px] leading-relaxed text-[#6B7280]">{children}</p>
    </div>
  );
}
